import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Settings, Landmark, BarChart3, Trash2 } from 'lucide-react';
import { adminBusinesses, type Business } from '../../lib/api';

export default function AdminBusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    adminBusinesses.get(id!).then(({ business, userCount }) => {
      setBusiness(business);
      setUserCount(userCount);
      setName(business.name);
      setIsActive(!!business.is_active);
    }).catch(err => setError(err.message)).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMsg('');
    setSaveLoading(true);
    try {
      const { business } = await adminBusinesses.update(id!, { name, is_active: isActive as any });
      setBusiness(business);
      setSaveMsg('Business updated');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Permanently delete "${business?.name}"? This removes every user, department, budget, and expense in this business and cannot be undone.`)) return;
    setDeleteLoading(true);
    setError('');
    try {
      await adminBusinesses.remove(id!);
      navigate('/admin/businesses');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  if (error && !business) return <div className="card text-red-600 text-center py-10">{error}</div>;
  if (!business) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/businesses" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-green-600" /> {business.name}</h1>
          <p className="text-gray-500 text-sm">{userCount} users &bull; {business.currency}</p>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Link to={`/business/setup?business_id=${business.id}`} className="btn-primary flex items-center gap-2">
          <Settings className="w-4 h-4" /> Manage Org, Budgets & Users
        </Link>
        <Link to={`/finance?business_id=${business.id}`} className="btn-secondary flex items-center gap-2">
          <Landmark className="w-4 h-4" /> Finance Queue
        </Link>
        <Link to={`/reports?business_id=${business.id}`} className="btn-secondary flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Reports
        </Link>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900">Business Details</h2>
        {saveMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{saveMsg}</div>}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input type="text" className="input-field" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4" />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
          <button type="submit" className="btn-primary" disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      <div className="card space-y-3 border-red-200">
        <h2 className="font-semibold text-red-700">Danger Zone</h2>
        <p className="text-sm text-gray-500">
          Permanently delete this business, including all its users, departments, budgets, expenses, and purchase requests.
        </p>
        <button onClick={handleDelete} className="btn-danger flex items-center gap-2 w-fit" disabled={deleteLoading}>
          <Trash2 className="w-4 h-4" /> {deleteLoading ? 'Deleting...' : 'Delete Business'}
        </button>
      </div>
    </div>
  );
}
